import React, { ReactElement, FunctionComponent, memo } from 'react'
import { TestID } from '../utils/PropTypesDefinitions'
import CardHeadlineWithIconComponent, { CardHeadlineWithIconProps } from './WithIcon'
import CardHeadlineWithTextComponent, { CardHeadlineWithTextProps } from './WithSelectableText'
import CardHeadlineWithHintComponent, { CardHeadlineWithHintProps } from './WithHint'
import DefaultCardHeadlineComponent, { DefaultCardHeadlineProps } from './DefaultCardHeadline'

export type CardHeadlineWithRightItemProps = {
    /** text shown on the right side */
    hint?: never
    /**
     * function that will be called when the item on the right is clicked
     * @TJS-type function
     */
    onRightItemPress?: () => void
    /**
     * function that will be called when the headline is clicked
     * @TJS-type function
     */
    onPress?: never
    /** colors the corresponding image in black or white */
    tintColor?: never
}

export type BasicCardHeadlineProps = {
    /** title of the card */
    text: string
    /** color of the text */
    textColor?: 'white' | 'onyx' | 'pearl'
    /** backgroundColor of the card */
    backgroundColor?: 'pearl' | 'TRANSPARENT'
    /** label to show on the right side of the component, can be used to identify this component as new  */
    infoLabel?: string
} & TestID

export type CardHeadlineProps =
    | CardHeadlineWithTextProps
    | CardHeadlineWithIconProps
    | DefaultCardHeadlineProps
    | CardHeadlineWithHintProps

const CardHeadline: FunctionComponent<CardHeadlineProps> = (props): ReactElement => {
    if (props.type === 'ICON') {
        const {
            type,
            text,
            onRightItemPress,
            testID,
            icon,
            webHeadlineType,
            textColor,
            backgroundColor,
            imageType,
            imageUrl,
            infoLabel,
        } = props
        return (
            <CardHeadlineWithIconComponent
                type={type}
                text={text}
                icon={icon}
                webHeadlineType={webHeadlineType}
                onRightItemPress={onRightItemPress}
                textColor={textColor}
                backgroundColor={backgroundColor}
                infoLabel={infoLabel}
                testID={testID}
                imageType={imageType}
                imageUrl={imageUrl}
            />
        )
    } else if (props.type === 'SELECTABLE_TEXT') {
        const {
            type,
            text,
            onRightItemPress,
            testID,
            selectableText,
            webHeadlineType,
            textColor,
            backgroundColor,
            imageType,
            imageUrl,
            infoLabel,
        } = props
        return (
            <CardHeadlineWithTextComponent
                type={type}
                text={text}
                selectableText={selectableText}
                webHeadlineType={webHeadlineType}
                onRightItemPress={onRightItemPress}
                textColor={textColor}
                backgroundColor={backgroundColor}
                infoLabel={infoLabel}
                testID={testID}
                imageType={imageType}
                imageUrl={imageUrl}
            />
        )
    } else if (props.type === 'HINT') {
        return <CardHeadlineWithHintComponent {...props} />
    }
    const {
        type,
        text,
        href,
        onPress,
        testID,
        tintColor,
        webHeadlineType,
        textColor,
        backgroundColor,
        imageType,
        imageUrl,
        infoLabel,
    } = props
    return (
        <DefaultCardHeadlineComponent
            type={type}
            text={text}
            onPress={onPress}
            href={href}
            tintColor={tintColor}
            webHeadlineType={webHeadlineType}
            textColor={textColor}
            backgroundColor={backgroundColor}
            infoLabel={infoLabel}
            testID={testID}
            imageType={imageType}
            imageUrl={imageUrl}
        />
    )
}

export default memo(CardHeadline)
